import React, { useContext } from 'react';
import Modal from 'react-bootstrap/Modal';
import Card from 'react-bootstrap/Card';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Stack from 'react-bootstrap/Stack';
import ProgressBar from 'react-bootstrap/ProgressBar';
import Dropdown from 'react-bootstrap/Dropdown';
import {
  useViewBooking,
  useUpdateBookingStatus,
  useGetAvailableTables,
  useAssignTable,
  sendTableToPos
} from '../../../../APIs/BookingManagement/booking';
import CancelBookingModal from '../CancelBooking/CancelBookingModal';
import LoadingSpinner from '../../../../components/common/LoadingSpinner/LoadingSpinner';
import { confirmAction } from '../../../../components/common/ConfirmationModal';
import { ConfigContext } from '../../../../contexts/ConfigContext';
import { notify } from '@common/NotificationService';
import usePermission from '../../../../hooks/usePermission';
import useBusinessConfig from '../../../../hooks/useBusinessConfig';
import useAuth from '../../../../auth/useAuth';
import './ViewBookingModal.css';

export default function ViewBookingModal({ show, onHide, bookingId, refresh }) {
  const config = useContext(ConfigContext);
  const { has } = usePermission();
  const { ui: bizUi, isHidden } = useBusinessConfig();
  const { disabledModules = [] } = useAuth();
  // No table layout for this business — by trade, or switched off in Feature
  // Settings. Then confirming a booking must not open (or flash) the table picker.
  const tableLayoutHidden = isHidden('table_layout') || disabledModules.includes('table_layout');

  const [commentModalShow, setCommentModalShow] = React.useState(false);
  const [cancelModalShow, setCancelModalShow] = React.useState(false);
  const [isStatusChanging, setIsStatusChanging] = React.useState(false);
  const [showForceAssignModal, setShowForceAssignModal] = React.useState(false);
  const [availableFloors, setAvailableFloors] = React.useState([]);
  const [selectedTableId, setSelectedTableId] = React.useState('');
  // The picker now opens already-populated (tables are fetched first), so this
  // stays false — kept only so the modal's guarded branches read a defined value.
  const [isFetchingTables] = React.useState(false);
  const [isAssigning, setIsAssigning] = React.useState(false);

  const { booking, isLoading, refresh: viewRefresh } = useViewBooking(bookingId);
  const { updateStatus } = useUpdateBookingStatus();
  const { fetchTables } = useGetAvailableTables();
  const { assignTable } = useAssignTable();

  React.useEffect(() => {
    setShowForceAssignModal(false);
    setAvailableFloors([]);
    setSelectedTableId('');
  }, [bookingId]);

  if (!show) return null;

  // if (!booking && !isLoading) {
  //   return (
  //     <Modal show={show} onHide={onHide} centered>
  //       <Modal.Header closeButton>
  //         <Modal.Title>Booking Details</Modal.Title>
  //       </Modal.Header>
  //       <Modal.Body>No booking selected.</Modal.Body>
  //     </Modal>
  //   );
  // }

  const {
    booking_id,
    booking_no,
    booking_date,
    booking_date_formatted,
    booking_time,
    booking_time_formatted,
    booking_type,
    location,
    service_period,
    status_label,
    booking_status,
    customer,
    internal_notes,
    comment,
    progress,
    amenities,
    no_of_guests,
    pre_orders,
    pre_order_total,
    has_seat,
    is_manual_assign,
    tables: assignedTables
  } = booking || {};

  const isTakeaway = booking_type === 'takeaway';

  const displayId = booking_no || booking_id || booking?.id || '-';
  const displayDate = booking_date_formatted || booking_date || booking?.date || '-';
  const displayTime = booking_time_formatted || booking_time || booking?.time || '-';
  const displayComment = internal_notes || comment || '—';

  // const locationLabel = `${booking?.city,} ${location?.name}` || '-';
  const locationLabel = [booking?.city, location?.name].filter(Boolean).join(', ') || '-';

  const statusLabel = status_label || booking_status || '-';
  // Logic reads the raw status, never the label. They used to be the same string
  // lowercased, so this worked by accident — until "sent_to_pos" started
  // rendering as "Sent to POS" and every comparison below silently missed.
  const normalizedStatus = (booking_status || status_label || '').toLowerCase();
  const preferences = amenities || booking?.preferences || [];
  const displayCustomerName =
    customer?.name && customer.name.length ? `${customer.name.charAt(0).toUpperCase()}${customer.name.slice(1)}` : 'Customer';

  const canAssignTable = normalizedStatus === 'pending' && !is_manual_assign && has('booking_confirm');
  const isManualPending = normalizedStatus === 'pending' && !!is_manual_assign;

  const canCancel = ['pending', 'confirmed'].includes(normalizedStatus) && has('booking_cancel');

  const canArrive = normalizedStatus === 'confirmed' && has('booking_arrive');

  const canComplete =
    normalizedStatus !== 'pending' &&
    normalizedStatus !== 'cancelled' &&
    normalizedStatus !== 'completed' &&
    normalizedStatus !== 'expired' &&
    has('booking_complete');

  // The sitting this booking is at, if any. Sending to the till acts on that
  // row rather than on the booking, so without one there is nothing to send.
  const sitting = (assignedTables || []).find((t) => t.assignment_id) || null;

  // Only a table with guests at it can be handed over: they have to be seated
  // (arrived), and it must not already be with the till or finished.
  const canSendToPos =
    !isTakeaway &&
    !!sitting &&
    normalizedStatus === 'arrived' &&
    !['sent_to_pos', 'completed', 'cancelled'].includes(sitting.assignment_status) &&
    has('table_assign');

  const isExpired = normalizedStatus === 'expired';

  // const derivedProgress = normalizedStatus.includes('cancel')
  //   ? 0
  //   : normalizedStatus.includes('complete')
  //     ? 100
  //     : normalizedStatus.includes('arrive')
  //       ? 80
  //       : normalizedStatus.includes('confirm')
  //         ? 60
  //         : normalizedStatus.includes('pending')
  //           ? 40
  //           : typeof progress === 'number'
  //             ? progress
  //             : null;

  const derivedProgress = isExpired
    ? 100
    : normalizedStatus.includes('cancel')
      ? 0
      : normalizedStatus.includes('complete')
        ? 100
        : normalizedStatus.includes('arrive')
          ? 80
          : normalizedStatus.includes('confirm')
            ? 60
            : normalizedStatus.includes('pending')
              ? 40
              : typeof progress === 'number'
                ? progress
                : null;

  // const progressVariant = normalizedStatus.includes('cancel')
  //   ? 'danger'
  //   : normalizedStatus.includes('complete')
  //     ? 'success'
  //     : normalizedStatus.includes('arrive')
  //       ? 'info'
  //       : derivedProgress >= 60
  //         ? 'primary'
  //         : derivedProgress >= 30
  //           ? 'warning'
  //           : 'secondary';

  const progressVariant = isExpired
    ? 'secondary'
    : normalizedStatus.includes('cancel')
      ? 'danger'
      : normalizedStatus.includes('complete')
        ? 'success'
        : normalizedStatus.includes('arrive')
          ? 'info'
          : derivedProgress >= 60
            ? 'primary'
            : derivedProgress >= 30
              ? 'warning'
              : 'secondary';

  const isChildModalOpen = commentModalShow || cancelModalShow || showForceAssignModal;

  const updateBookingStatus = async (action, extraPayload = {}) => {
    try {
      const payload = {
        action,
        ...extraPayload
      };

      const res = await updateStatus(bookingId, payload);

      if (res?.success) {
        notify.success(res?.message || 'Status updated successfully', 'update');
        await refresh();
        await viewRefresh();
        return true;
      } else {
        notify.error(res?.message || 'Failed to update booking status', 'update');
        return false;
      }
    } catch (error) {
      notify.error(error?.response?.data?.message || 'Something went wrong', 'update');
      return false;
    }
  };

  const confirmAndRun = async (title, message, actionFn) => {
    const ok = await confirmAction({
      title,
      message,
      confirmText: 'Yes',
      cancelText: 'No'
    });
    if (!ok) return false;
    try {
      setIsStatusChanging(true);
      await actionFn();
      return true;
    } finally {
      setIsStatusChanging(false);
    }
  };

  const handleMarkArrive = async () =>
    confirmAndRun('Mark as arrived?', 'Confirm that the guest has arrived?', async () => {
      await updateBookingStatus('arrive');
    });

  const handleMarkComplete = async () =>
    confirmAndRun('Mark as complete?', 'Do you want to mark this booking as complete?', async () => {
      await updateBookingStatus('complete');
    });

  const handleSendToPos = async () =>
    confirmAndRun('Send to POS?', 'The table will be handed to the till so the bill can be settled.', async () => {
      try {
        const res = await sendTableToPos(sitting?.assignment_id);

        if (res?.success) {
          // The message names the open bill when there is one, so the cashier
          // knows what they are about to be handed.
          const bill = res?.open_bill;
          notify.success(bill ? `Sent to POS — bill ${bill.bill_no} ($${bill.total_amount})` : res.message || 'Sent to POS', 'update');
          await refresh();
          await viewRefresh();
          return;
        }

        notify.error(res?.message || 'Could not send this table to the POS', 'update');
      } catch (err) {
        notify.error(err?.response?.data?.message || 'Could not send this table to the POS', 'update');
      }
    });

  const handleConfirmClick = async () => {
    // Trades with no floor layout have no table to assign, so the picker would
    // open empty with no way out. Confirm directly instead — deliberately NOT
    // the manual-assign path, which leaves the booking pending (see
    // BookingManagementController: "mark as manual assign → stays pending"),
    // and would strand every salon booking as unconfirmed.
    if (!bizUi.tableAssignment || tableLayoutHidden) {
      setIsStatusChanging(true);
      await updateBookingStatus('confirm');
      setIsStatusChanging(false);
      return;
    }

    // Fetch the layout BEFORE opening the picker — opening it first and closing
    // it once the fetch comes back empty is what made the modal flash. The
    // Confirm button shows its disabled/busy state while this runs.
    setIsStatusChanging(true);
    setSelectedTableId('');
    const result = await fetchTables(bookingId);
    setIsStatusChanging(false);

    // A restaurant that has not drawn its floor plan yet — no floors, or floors
    // with no tables on them. The picker would open empty and the only way out
    // would be manual assign, which leaves the booking pending forever. Nothing
    // is assignable and nothing ever will be until a layout exists, so confirm.
    if (!result.hasLayout) {
      setIsStatusChanging(true);
      await updateBookingStatus('confirm');
      setIsStatusChanging(false);
      return;
    }

    // Layout exists — now open the picker, already populated.
    setAvailableFloors(result.floors || []);
    setShowForceAssignModal(true);
  };

  const handleAssignTable = async () => {
    if (!selectedTableId) return;
    setIsAssigning(true);
    try {
      const res = await assignTable(bookingId, { table_id: Number(selectedTableId) });
      if (res?.success) {
        notify.success(res?.message || 'Table assigned and booking confirmed');
        setShowForceAssignModal(false);
        await refresh();
        await viewRefresh();
      } else {
        notify.error(res?.message || 'Failed to assign table');
      }
    } catch (error) {
      notify.error(error?.response?.data?.message || 'Something went wrong');
    }
    setIsAssigning(false);
  };

  const handleManualAssign = async () => {
    setIsAssigning(true);
    try {
      const res = await assignTable(bookingId, { manual: true });
      if (res?.success) {
        notify.success(res?.message || 'Booking marked for manual assignment');
        setShowForceAssignModal(false);
        await refresh();
        await viewRefresh();
      } else {
        notify.error(res?.message || 'Failed to set manual assignment');
      }
    } catch (error) {
      notify.error(error?.response?.data?.message || 'Something went wrong');
    }
    setIsAssigning(false);
  };

  const isDark = config.mode === 'dark';
  const textColor = isDark ? '#e6e6e6' : '#000';
  const mutedColor = isDark ? '#a0a0a0' : '#6c757d';
  const borderColor = isDark ? '#333' : '#eef0f5';

  return (
    <>
      <Modal show={show} onHide={onHide} centered size="md" className={`view-booking-modal ${isChildModalOpen ? 'is-blurred' : ''}`}>
        <Modal.Header closeButton>
          {isLoading ? (
            <Modal.Title>Booking Details</Modal.Title>
          ) : (
            <Stack direction="horizontal" className="w-100 justify-content-between align-items-center">
              <div className="d-flex align-items-center gap-2">
                <span className="fw-bold fs-5">{displayId}</span>
                <i className="ph ph-caret-right fw-bold small" />
                <span className="text-muted">{displayCustomerName}</span>
              </div>
            </Stack>
          )}
        </Modal.Header>
        <Modal.Body>
          {isLoading ? (
            <div className="py-5 d-flex justify-content-center align-items-center" style={{ minHeight: '200px' }}>
              <LoadingSpinner />
            </div>
          ) : (
            <>
              <Card className="border-0 shadow-none" style={{ borderColor: borderColor, backgroundColor: isDark ? '#1e1e1e' : '#fff' }}>
                <Card.Body className="py-3 border-bottom" style={{ borderColor: borderColor }}>
                  {/* Action row */}
                  <div className="d-flex align-items-center justify-content-between">
                    <div className="d-flex align-items-center gap-2">
                      <i className="ph ph-calendar-blank align-middle f-20 text-primary" />
                      <span className="f-16" style={{ color: mutedColor }}>
                        {displayDate}
                      </span>
                    </div>
                    <div className="d-flex align-items-center gap-3">
                      {isManualPending ? (
                        <>
                          <span
                            className="badge"
                            style={{
                              background: 'rgba(234,179,8,0.15)',
                              color: '#ca8a04',
                              borderRadius: 12,
                              fontSize: '0.7rem',
                              fontWeight: 600
                            }}
                          >
                            <i className="ph ph-device-mobile me-1" />
                            Pending App
                          </span>
                          {canCancel && (
                            <button
                              type="button"
                              className="btn btn-sm bm-action-btn bm-action-btn--cancel d-inline-flex align-items-center"
                              disabled={isStatusChanging}
                              onClick={() => setCancelModalShow(true)}
                              title="Cancel Booking"
                            >
                              <i className="ph ph-x f-14" />
                              Cancel
                            </button>
                          )}
                        </>
                      ) : (
                        <>
                          {!isTakeaway && canAssignTable && (
                            <button
                              type="button"
                              className="btn btn-sm bm-action-btn bm-action-btn--confirm d-inline-flex align-items-center"
                              disabled={isStatusChanging}
                              onClick={handleConfirmClick}
                              title="Confirm Booking"
                            >
                              <i className="ph ph-check-circle f-14" />
                              Confirm Booking
                            </button>
                          )}
                          {canCancel && (
                            <button
                              type="button"
                              className="btn btn-sm bm-action-btn bm-action-btn--cancel d-inline-flex align-items-center"
                              disabled={isStatusChanging}
                              onClick={() => setCancelModalShow(true)}
                              title="Cancel Booking"
                            >
                              <i className="ph ph-x f-14" />
                              Cancel
                            </button>
                          )}
                          {!isTakeaway && (
                            <button
                              type="button"
                              className="btn p-0 bm-icon-btn"
                              onClick={() => setCommentModalShow(true)}
                              title="Admin Comment"
                            >
                              <i className="ph ph-chat f-18 text-success-custom" />
                            </button>
                          )}
                          {isTakeaway ? (
                            <button
                              type="button"
                              className="btn btn-sm bm-action-btn bm-action-btn--confirm d-inline-flex align-items-center"
                              disabled={!canComplete || isStatusChanging}
                              onClick={handleMarkComplete}
                            >
                              <i className="ph ph-check-fat f-14" />
                              Complete
                            </button>
                          ) : (
                            <Dropdown align="end">
                              <Dropdown.Toggle as="a" href="#!" bsPrefix="false" className="text-secondary p-0 border-0 bm-icon-btn">
                                <i className="ph ph-gear f-18" />
                              </Dropdown.Toggle>
                              <Dropdown.Menu>
                                <Dropdown.Item
                                  disabled={!canArrive || isStatusChanging}
                                  onClick={async (e) => {
                                    e.preventDefault();
                                    if (!canArrive) return;
                                    await handleMarkArrive();
                                  }}
                                >
                                  <i className="ph ph-check-circle me-2 text-info" />
                                  Mark as Arrive
                                </Dropdown.Item>
                                <hr className="m-0 my-2" />
                                <Dropdown.Item
                                  disabled={!canSendToPos || isStatusChanging}
                                  onClick={async (e) => {
                                    e.preventDefault();
                                    if (!canSendToPos) return;
                                    await handleSendToPos();
                                  }}
                                >
                                  <i className="ph ph-cash-register me-2 text-warning" />
                                  Send to POS
                                </Dropdown.Item>
                                <hr className="m-0 my-2" />
                                <Dropdown.Item
                                  disabled={!canComplete || isStatusChanging}
                                  onClick={async (e) => {
                                    e.preventDefault();
                                    if (!canComplete) return;
                                    await handleMarkComplete();
                                  }}
                                >
                                  <i className="ph ph-check-fat me-2 text-success" />
                                  Mark as Complete
                                </Dropdown.Item>
                              </Dropdown.Menu>
                            </Dropdown>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                </Card.Body>

                <Card.Body className="pb-3 border-bottom" style={{ borderColor: borderColor }}>
                  <div className="bm-customer-comment">
                    <div className="bm-customer-comment__label">
                      <i className="ph ph-chat-dots" />
                      Customer comment
                    </div>
                    <p className="bm-customer-comment__text">{booking?.customer_comment || '—'}</p>
                  </div>
                </Card.Body>

                <Card.Body>
                  <Row className="g-3">
                    <Col md={6}>
                      <div className="d-flex align-items-center gap-1">
                        <div className="small d-flex align-items-center gap-1 me-2" style={{ color: mutedColor }}>
                          <i className="ph ph-chart-bar text-primary f-16" />
                          <span>Service period</span>
                        </div>
                        <div className="fw-semibold" style={{ color: textColor }}>
                          {service_period?.name || '-'}
                        </div>
                      </div>
                    </Col>
                    <Col md={6}>
                      <div className="d-flex align-items-center gap-1">
                        <div className="small d-flex align-items-center gap-1 me-2" style={{ color: mutedColor }}>
                          <i className="ph ph-phone text-primary f-16" />
                          <span>Phone number</span>
                        </div>
                        <div className="fw-semibold" style={{ color: textColor }}>
                          {customer?.phone || '-'}
                        </div>
                      </div>
                    </Col>

                    <Col md={6}>
                      <div className="d-flex align-items-center gap-1">
                        <div className="small d-flex align-items-center gap-1 me-2" style={{ color: mutedColor }}>
                          <i className="ph ph-clock text-primary f-16" />
                          <span>Time</span>
                        </div>
                        <div className="fw-semibold" style={{ color: textColor }}>
                          {displayTime}
                        </div>
                      </div>
                    </Col>
                    <Col md={6}>
                      <div className="d-flex align-items-center gap-1">
                        <div className="small d-flex align-items-center gap-1 me-2" style={{ color: mutedColor }}>
                          <i className="ph ph-envelope-simple text-primary f-16" />
                          <span>Email</span>
                        </div>
                        <div className="fw-semibold" style={{ color: textColor }}>
                          {customer?.email || '-'}
                        </div>
                      </div>
                    </Col>

                    {!isTakeaway && (
                      <Col md={6}>
                        <div className="d-flex align-items-center gap-1">
                          <div className="small d-flex align-items-center gap-1 me-2" style={{ color: mutedColor }}>
                            <i className="ph ph-user text-primary f-16" />
                            <span>Guest count</span>
                          </div>
                          <div className="fw-semibold" style={{ color: textColor }}>
                            {no_of_guests || booking?.guests || booking?.no_of_guests || '-'}
                          </div>
                        </div>
                      </Col>
                    )}
                    <Col md={6}>
                      <div className="d-flex align-items-center gap-1">
                        <div className="small d-flex align-items-center gap-1 me-2" style={{ color: mutedColor }}>
                          <i className="ph ph-map-pin-line text-primary f-16" />
                          <span>Location</span>
                        </div>
                        <div className="fw-semibold" style={{ color: textColor }}>
                          {locationLabel}
                        </div>
                      </div>
                    </Col>

                    {!isTakeaway && (
                      <Col md={6}>
                        <div className="d-flex align-items-center gap-1">
                          <div className="small d-flex align-items-center gap-1 me-2" style={{ color: mutedColor }}>
                            <i className="ph ph-chair text-primary f-16" />
                            <span>Table</span>
                          </div>
                          <div className="fw-semibold" style={{ color: textColor }}>
                            {has_seat && assignedTables?.length > 0 ? (
                              assignedTables.map((t, i) => (
                                <span key={t.id}>
                                  {i > 0 && <span className="text-muted mx-1">+</span>}
                                  {t.name}
                                  <span className="text-muted small ms-1">(cap. {t.capacity})</span>
                                </span>
                              ))
                            ) : is_manual_assign ? (
                              <span style={{ color: '#ca8a04', fontSize: '0.8rem', fontWeight: 600 }}>Pending App</span>
                            ) : (
                              <span style={{ color: mutedColor }}>—</span>
                            )}
                          </div>
                        </div>
                      </Col>
                    )}

                    {/* {derivedProgress !== null ? (
                      <Col md={12}>
                        <div className="small mb-1" style={{ color: mutedColor }}>
                          Booking Progress
                        </div>
                        <ProgressBar now={derivedProgress} variant={progressVariant} style={{ height: 6 }} />
                      </Col>
                    ) : null} */}
                    {/* <Col md={12}>
                      <div className="small mb-3" style={{ color: mutedColor }}>
                        Booking Progress
                      </div>

                      {normalizedStatus.includes('cancel' || 'cancelled') ? (
                        <div className="cancelled-strip">Cancelled</div>
                      ) : (
                        <ProgressBar now={derivedProgress} variant={progressVariant} style={{ height: 6 }} />
                      )}
                    </Col> */}
                    <Col md={12}>
                      <div className="small mb-3" style={{ color: mutedColor }}>
                        Booking Progress
                      </div>

                      {
                        // normalizedStatus.includes('cancel') ? (
                        //   <div className="cancelled-strip">Cancelled</div>
                        // ) :
                        normalizedStatus.includes('cancel') || normalizedStatus === 'expired' ? (
                          <div className={`cancelled-strip ${normalizedStatus.includes('cancel') ? 'cancelled' : 'expired'}`}>
                            {normalizedStatus.includes('cancel') ? 'Cancelled' : 'Expired'}
                          </div>
                        ) : (
                          <div className="booking-progress-container">
                            {/* Floating Label */}
                            <div
                              className={`booking-progress-label ${progressVariant}`}
                              style={{ left: `calc(${derivedProgress}% - 40px)` }}
                            >
                              {statusLabel}
                            </div>

                            {/* Progress bar */}
                            <ProgressBar now={derivedProgress} variant={progressVariant} className="booking-progress-bar" />

                            {/* Marker circle */}
                            <div
                              className={`booking-progress-marker ${progressVariant}`}
                              style={{ left: `calc(${derivedProgress}% - 7px)` }}
                            ></div>
                          </div>
                        )
                      }
                    </Col>

                    {Array.isArray(preferences) && preferences.length > 0 ? (
                      <Col md={12}>
                        <div className="small mb-3" style={{ color: mutedColor }}>
                          Preferences
                        </div>
                        <div className="d-flex flex-wrap gap-2">
                          {preferences.map((pref) => (
                            <div key={pref.id || pref.name || pref} className="pref-pill">
                              <span className="pref-pill-icon">
                                <i className="ph ph-check"></i>
                              </span>
                              <span className="pref-pill-label">{pref.name || pref}</span>
                            </div>
                          ))}
                        </div>
                      </Col>
                    ) : null}

                    {Array.isArray(pre_orders) && pre_orders.length > 0 && (
                      <Col md={12}>
                        <div className="small fw-semibold mb-2 d-flex align-items-center gap-1" style={{ color: mutedColor }}>
                          <i className="ph ph-fork-knife text-primary f-15" />
                          Pre-ordered Items
                        </div>
                        <div className="vbm-preorder-table">
                          {pre_orders.map((item) => (
                            <div key={item.id} className="vbm-preorder-row">
                              <div>
                                <div className="vbm-preorder-name">{item.name}</div>
                                <div className="vbm-preorder-meta">
                                  {item.category} &middot; ${Number(item.unit_price).toFixed(2)} &times; {item.quantity}
                                </div>
                              </div>
                              <div className="vbm-preorder-subtotal">${Number(item.subtotal).toFixed(2)}</div>
                            </div>
                          ))}
                          <div className="vbm-preorder-total">
                            <span>Total</span>
                            <span>${Number(pre_order_total ?? 0).toFixed(2)}</span>
                          </div>
                        </div>
                      </Col>
                    )}
                  </Row>
                </Card.Body>
              </Card>
            </>
          )}
        </Modal.Body>
      </Modal>

      <Modal
        show={commentModalShow}
        onHide={() => setCommentModalShow(false)}
        centered
        size="sm"
        className="admin-comment-modal"
        backdrop="static"
      >
        <Modal.Header closeButton>
          <Modal.Title>Admin&apos;s Comment</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="d-flex align-items-start gap-2">
            <span className="fw-semibold" style={{ color: mutedColor }}>
              Comment:{' '}
            </span>
            <span style={{ color: mutedColor, wordBreak: 'break-word', whiteSpace: 'pre-wrap' }}>{displayComment || '—'}</span>
          </div>
        </Modal.Body>
      </Modal>

      <CancelBookingModal
        show={cancelModalShow}
        booking={booking}
        onHide={() => setCancelModalShow(false)}
        onSubmit={async ({ mode, reason }) => {
          setCancelModalShow(false);

          await updateBookingStatus('cancel', {
            cancellation_source: mode,
            cancellation_reason: reason
          });
        }}
        config={config}
      />

      {/* Forced table assign modal — no dismiss, only assign or manual assign */}
      <Modal show={showForceAssignModal} centered size="md" backdrop="static" keyboard={false}>
        <Modal.Header style={{ borderColor: borderColor, backgroundColor: isDark ? '#1a2335' : '#fff' }}>
          <div>
            <Modal.Title style={{ fontSize: '1rem', color: textColor, display: 'flex', alignItems: 'center', gap: 8 }}>
              <i className="ph ph-chair text-primary" />
              Assign Table to Confirm
            </Modal.Title>
            <p style={{ margin: 0, marginTop: 3, fontSize: '0.78rem', color: mutedColor }}>
              Select a table below — booking confirms on assignment
            </p>
          </div>
        </Modal.Header>

        <Modal.Body style={{ backgroundColor: isDark ? '#1e1e1e' : '#f8f9fc', padding: '16px 20px', maxHeight: 380, overflowY: 'auto' }}>
          {isFetchingTables ? (
            <div className="py-4 d-flex justify-content-center">
              <LoadingSpinner />
            </div>
          ) : availableFloors.length > 0 ? (
            availableFloors.map((floor) => (
              <div key={floor.floor_id} style={{ marginBottom: 20 }}>
                {/* Floor heading with divider */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
                  <i className="ph ph-map-trifold" style={{ fontSize: 13, color: mutedColor, flexShrink: 0 }} />
                  <span
                    style={{
                      fontSize: '0.7rem',
                      fontWeight: 700,
                      color: mutedColor,
                      textTransform: 'uppercase',
                      letterSpacing: '0.07em',
                      whiteSpace: 'nowrap'
                    }}
                  >
                    {floor.floor_name}
                  </span>
                  <div style={{ flex: 1, height: 1, background: borderColor }} />
                </div>

                {/* Table cards */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(105px, 1fr))', gap: 8 }}>
                  {floor.tables.map((t) => {
                    const sel = String(t.id) === String(selectedTableId);
                    return (
                      <div
                        key={t.id}
                        onClick={() => setSelectedTableId(String(t.id))}
                        style={{
                          position: 'relative',
                          padding: '12px 8px 10px',
                          borderRadius: 10,
                          border: `2px solid ${sel ? '#4e73df' : isDark ? '#2a3a52' : '#e2e8f0'}`,
                          background: sel ? (isDark ? 'rgba(78,115,223,0.18)' : 'rgba(78,115,223,0.07)') : isDark ? '#1a2840' : '#fff',
                          cursor: 'pointer',
                          textAlign: 'center',
                          boxShadow: sel ? '0 0 0 3px rgba(78,115,223,0.15)' : '0 1px 4px rgba(0,0,0,0.06)',
                          transition: 'border-color 0.15s, box-shadow 0.15s, background 0.15s'
                        }}
                        onMouseEnter={(e) => {
                          if (!sel) e.currentTarget.style.borderColor = isDark ? '#4e73df88' : '#a5b4fc';
                        }}
                        onMouseLeave={(e) => {
                          if (!sel) e.currentTarget.style.borderColor = isDark ? '#2a3a52' : '#e2e8f0';
                        }}
                      >
                        {/* Selected checkmark dot */}
                        {sel && (
                          <div
                            style={{
                              position: 'absolute',
                              top: 5,
                              right: 5,
                              width: 16,
                              height: 16,
                              borderRadius: '50%',
                              background: '#4e73df',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center'
                            }}
                          >
                            <i className="ph ph-check" style={{ fontSize: 9, color: '#fff' }} />
                          </div>
                        )}
                        <i
                          className="ph ph-chair"
                          style={{
                            fontSize: 22,
                            color: sel ? '#4e73df' : mutedColor,
                            display: 'block',
                            marginBottom: 6
                          }}
                        />
                        <div
                          style={{
                            fontWeight: 600,
                            fontSize: '0.78rem',
                            lineHeight: 1.2,
                            color: sel ? (isDark ? '#93b4ff' : '#3b5bdb') : textColor,
                            marginBottom: 5,
                            wordBreak: 'break-word'
                          }}
                        >
                          {t.name}
                        </div>
                        <span
                          style={{
                            fontSize: '0.67rem',
                            color: sel ? (isDark ? '#93b4ff' : '#4e73df') : mutedColor,
                            background: sel
                              ? isDark
                                ? 'rgba(78,115,223,0.2)'
                                : 'rgba(78,115,223,0.1)'
                              : isDark
                                ? 'rgba(255,255,255,0.06)'
                                : 'rgba(0,0,0,0.05)',
                            borderRadius: 4,
                            padding: '2px 6px',
                            display: 'inline-block',
                            fontWeight: sel ? 600 : 400
                          }}
                        >
                          {t.capacity} seats
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))
          ) : (
            <div style={{ textAlign: 'center', padding: '24px 0' }}>
              <i className="ph ph-chair" style={{ fontSize: 36, color: mutedColor, display: 'block', marginBottom: 8 }} />
              <p style={{ color: mutedColor, fontSize: '0.85rem', margin: 0 }}>No tables with matching capacity are free at this time.</p>
              <p style={{ color: mutedColor, fontSize: '0.8rem', marginTop: 4 }}>Use manual assign to flag for later.</p>
            </div>
          )}
        </Modal.Body>

        <Modal.Footer style={{ borderColor: borderColor, backgroundColor: isDark ? '#1a2335' : '#fff', justifyContent: 'space-between' }}>
          <button
            disabled={isAssigning}
            onClick={handleManualAssign}
            className="btn btn-sm bm-action-btn d-inline-flex align-items-center"
            style={{ backgroundColor: 'rgba(234,179,8,0.12)', color: '#ca8a04' }}
          >
            <i className="ph ph-device-mobile f-14" />
            {isAssigning ? 'Saving...' : 'Manual Assign'}
          </button>
          <button
            disabled={!selectedTableId || isAssigning || isFetchingTables}
            onClick={handleAssignTable}
            className="btn btn-sm bm-action-btn bm-action-btn--confirm d-inline-flex align-items-center"
          >
            <i className="ph ph-check f-14" />
            {isAssigning ? 'Assigning...' : 'Assign & Confirm'}
          </button>
        </Modal.Footer>
      </Modal>
    </>
  );
}
